import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Pencil, RefreshCw, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, type OrgDetail } from '@/lib/api/admin';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/admin/orgs/$orgId')({
  component: OrgDetailPage,
});

function RoleBadge({ role }: { role: string }) {
  const cls: Record<string, string> = {
    org_admin: 'bg-primary/10 text-primary',
    trainer: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    student: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${cls[role] ?? 'bg-gray-100 text-gray-600'}`}>
      {role.replace('_', ' ')}
    </span>
  );
}

function OrgDetailPage() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [inviteActions, setInviteActions] = useState<Record<string, 'resending' | 'revoking'>>({});

  useEffect(() => {
    adminApi.getOrg(orgId).then(setOrg).finally(() => setLoading(false));
  }, [orgId]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await adminApi.deleteOrg(orgId);
      navigate({ to: '/admin/orgs' });
    } finally {
      setDeleting(false);
    }
  }

  async function handleResendInvite(inviteId: string, email: string) {
    setInviteActions((prev) => ({ ...prev, [inviteId]: 'resending' }));
    try {
      await adminApi.resendInvite(inviteId);
      toast.success(`Invite resent to ${email}`);
    } catch {
      toast.error('Failed to resend invite');
    } finally {
      setInviteActions((prev) => { const n = { ...prev }; delete n[inviteId]; return n; });
    }
  }

  async function handleRevokeInvite(inviteId: string) {
    setInviteActions((prev) => ({ ...prev, [inviteId]: 'revoking' }));
    try {
      await adminApi.deleteInvite(inviteId);
      setOrg((prev) =>
        prev
          ? {
              ...prev,
              pending_invites: prev.pending_invites.filter((i) => i.id !== inviteId),
              pending_count: Math.max(0, prev.pending_count - 1),
            }
          : prev
      );
      toast.success('Invite revoked');
    } catch {
      toast.error('Failed to revoke invite');
    } finally {
      setInviteActions((prev) => { const n = { ...prev }; delete n[inviteId]; return n; });
    }
  }

  async function handleSaveName() {
    if (savingName || !org) return;
    const trimmed = nameInput.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      setEditingName(false);
      return;
    }
    if (trimmed === org.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const updated = await adminApi.updateOrg(orgId, { name: trimmed });
      setOrg((prev) => prev ? { ...prev, name: updated.name } : prev);
      toast.success('Organization renamed');
    } catch {
      toast.error('Failed to rename organization');
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="h-4 w-64 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!org) {
    return <p className="text-muted-foreground">Organization not found.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/orgs" className="mb-3 flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Organizations
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 group">
              {editingName ? (
                <>
                  <input
                    autoFocus
                    className="text-2xl font-heading font-bold text-foreground bg-transparent border-b-2 border-primary outline-none w-auto min-w-0"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    onBlur={handleSaveName}
                    disabled={savingName}
                    maxLength={100}
                  />
                  {savingName && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                </>
              ) : (
                <>
                  <h1
                    className="text-2xl font-heading font-bold text-foreground cursor-pointer"
                    onClick={() => { setNameInput(org.name); setEditingName(true); }}
                  >{org.name}</h1>
                  <button
                    type="button"
                    onClick={() => { setNameInput(org.name); setEditingName(true); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                    aria-label="Rename organization"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {org.slug} · {org.member_count} members · {org.pending_count} pending
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{org.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes the organisation, all its batches, members, and pending invites. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting ? 'Deleting…' : 'Delete organisation'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
        <div className="border-b border-border px-5 py-3.5">
          <h2 className="text-[13px] font-semibold text-foreground">Members</h2>
        </div>
        {(org.members ?? []).length === 0 ? (
          <p className="px-5 py-6 text-[13px] text-muted-foreground">No members yet.</p>
        ) : (() => {
          // Deduplicate: one row per unique user, batches aggregated
          const seen = new Map<string, { email: string; username: string; role: string; batches: string[] }>();
          for (const m of org.members) {
            const key = `${m.user_id}-${m.role}`;
            if (!seen.has(key)) {
              seen.set(key, { email: m.email, username: m.username, role: m.role, batches: [] });
            }
            if (m.batch_name) seen.get(key)!.batches.push(m.batch_name);
          }
          const rows = Array.from(seen.values());
          return (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Email', 'Username', 'Role', 'Batches'].map((h) => (
                    <th key={h} className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {rows.map((m, i) => (
                  <tr key={i} className="hover:bg-muted/30">
                    <td className="px-5 py-2.5 text-[13px] text-foreground">{m.email}</td>
                    <td className="px-5 py-2.5 text-[13px] text-foreground">{m.username}</td>
                    <td className="px-5 py-2.5"><RoleBadge role={m.role} /></td>
                    <td className="px-5 py-2.5 text-[13px] text-muted-foreground">
                      {m.batches.length > 0
                        ? <span className="flex flex-wrap gap-1">{m.batches.map((b) => <span key={b} className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{b}</span>)}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
        <div className="border-b border-border px-5 py-3.5">
          <h2 className="text-[13px] font-semibold text-foreground">Pending Invites</h2>
        </div>
        {(org.pending_invites ?? []).length === 0 ? (
          <p className="px-5 py-6 text-[13px] text-muted-foreground">No pending invites.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Email', 'Role', 'Expires', ''].map((h) => (
                  <th key={h} className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {org.pending_invites.map((inv) => (
                <tr key={inv.id} className="hover:bg-muted/30">
                  <td className="px-5 py-2.5 text-[13px] text-foreground">{inv.email}</td>
                  <td className="px-5 py-2.5"><RoleBadge role={inv.role} /></td>
                  <td className="px-5 py-2.5 text-[12px] text-muted-foreground">
                    {new Date(inv.expires_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!!inviteActions[inv.id]}
                        onClick={() => handleResendInvite(inv.id, inv.email)}
                        className="flex items-center gap-1 rounded px-2 py-1 text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
                        aria-label={`Resend invite to ${inv.email}`}
                      >
                        {inviteActions[inv.id] === 'resending'
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <RefreshCw className="h-3 w-3" />}
                        Resend
                      </button>
                      <button
                        type="button"
                        disabled={!!inviteActions[inv.id]}
                        onClick={() => handleRevokeInvite(inv.id)}
                        className="flex items-center gap-1 rounded px-2 py-1 text-[12px] text-destructive/70 hover:text-destructive hover:bg-destructive/8 transition-colors disabled:opacity-40"
                        aria-label={`Revoke invite for ${inv.email}`}
                      >
                        {inviteActions[inv.id] === 'revoking'
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <X className="h-3 w-3" />}
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
